export type BrokerCohort = 'active' | 'deleted' | 'blocked' | 'dev' | 'internal'
export type BrokerTier = 1 | 2 | 3 | 4 | 5 | 6 | 7
export type BrokerPlan = 'No subscription' | 'Free' | 'Standard' | 'Premium'

export interface BrokerActivationInput {
    emailVerified: boolean
    identity: boolean
    rera: boolean
    plan: BrokerPlan
    units: number
    paidBricks: number
    isDev: boolean
    isInternal: boolean
    deleted: boolean
    blocked: boolean
}

export interface BrokerCohortCounts {
    active: number
    deleted: number
    blocked: number
    dev: number
    internal: number
    raw: number
}

// The order matters so every broker has one cohort and the compact +N badges
// reconcile exactly with the raw broker count.
export const getBrokerCohort = (input: Pick<BrokerActivationInput, 'deleted' | 'blocked' | 'isDev' | 'isInternal'>): BrokerCohort => {
    if (input.deleted) return 'deleted'
    if (input.blocked) return 'blocked'
    if (input.isDev) return 'dev'
    if (input.isInternal) return 'internal'
    return 'active'
}

export const buildBrokerCohortCounts = (items: BrokerActivationInput[]): BrokerCohortCounts =>
    items.reduce<BrokerCohortCounts>((counts, input) => {
        counts[getBrokerCohort(input)] += 1
        counts.raw += 1
        return counts
    }, { active: 0, deleted: 0, blocked: 0, dev: 0, internal: 0, raw: 0 })

// Progress is intentionally sequential: a broker cannot skip a prerequisite
// just because a later action exists in the data.
export const getBrokerTier = (input: BrokerActivationInput): BrokerTier => {
    if (!input.emailVerified) return 1
    if (!input.identity) return 2
    if (input.plan === 'No subscription') return 3
    if (input.units <= 0) return 4
    if (input.plan !== 'Premium') return 5
    if (input.paidBricks <= 0) return 6
    return 7
}

export const BROKER_TIER_LABELS: Record<BrokerTier, { title: string, note: string }> = {
    1: { title: 'Registered — email not verified', note: 'Email verification is the next step' },
    2: { title: 'Email verified — awaiting identity', note: 'Passport or Emirates ID is the next step' },
    3: { title: 'Identity document — awaiting subscription', note: 'A paid subscription is the next step' },
    4: { title: 'Subscribed — awaiting own unit', note: 'An owned unit is the next step' },
    5: { title: 'Own unit added — awaiting Premium', note: 'Premium is the next step' },
    6: { title: 'Premium active — awaiting paid Bricks', note: 'A paid Brick package is the next step' },
    7: { title: 'Paid Bricks buyer — complete', note: 'Activation complete' }
}
